import React, { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import {
  PEOPLE_STATS,
  CHANGE_PASSWORD,
  ACTIVITY_MONITORING,
  ACTIVITY_TREND,
  ADD_TEST_PERSON,
  MOBILITY_REPORT,
  ADMIN_CHOICE,
  EDIT_TEST_PERSON,
  FORGOT_PASSWORD,
  HOW_TO_TEST_TUG_FRONT,
  HOW_TO_TEST_TUG_SIDE,
  LOGIN,
  PERSON_DASHBOARD,
  SIGNUP,
  TEST_HISTORY,
  TUG_FRONT_INFO,
  TUG_SIDE_INFO,
  STS_SIDE_INFO,
  HOW_TO_TEST_STS_SIDE,
  USER_LIST,
  VIEW_TEST_PERSON,
  VITALS,
  ACTION_PLAN_REPORT,
  MOBILITY_REPORT_SHARED,
  GAZE_METRICS_REPORT,
} from "./routerPath";
import PeopleStats from "../components/peopleStats/PeopleStats";
import MmtChangePassword from "../components/mmtAuth/MmtChangePassword";
import Layout from "../Layout";
import App from "../App";
import HistoryTab from "../components/userDetail/history/HistoryTab";
import TugSide from "../components/userDetail/testModule/tug/tugSide/TugSide";
import TugFront from "../components/userDetail/testModule/tug/tugFront/TugFront";
import HowtoTestTugFront from "../components/userDetail/testModule/tug/howToTest/howToTestTugFront/HowtoTestTugFront";
import HowtoTestTugSide from "../components/userDetail/testModule/tug/howToTest/howToTestTugSide/HowtoTestTugSide";
import PersonDashboard from "../components/userDetail/personDashboard/PersonDashboard";
import ActivityMonitoring from "../components/userDetail/activityMonitoring/ActivityMonitoring";
import ActivityTrend from "../components/userDetail/activityMonitoring/ActivityTrend";
import MobilityReportV95 from "../components/userDetail/personDashboard/mobilityReport/MobilityReport";
import MobilityReportV2 from "../components/userDetail/personDashboard/mobilityReport/MobilityReportV2";
import MmtLogin from "../components/mmtAuth/MmtLogin";
import MmtSignupFlow from "../components/mmtAuth/MmtSignupFlow";
import MmtForgotPasswordFlow from "../components/mmtAuth/MmtForgotPasswordFlow";
import MmtUserList from "../components/mmtUserList/MmtUserList";
import MmtAddTestPerson from "../components/testPerson/MmtAddTestPerson";
import MmtEditTestPerson from "../components/testPerson/MmtEditTestPerson";
import RequireSession from "../components/mmtAuth/RequireSession";
import AdminChoice from "../components/mmtAuth/AdminChoice";
import VitalsScreen from "../components/userDetail/vitals/VitalsScreen";
import SharedActionPlanReport from "../components/userDetail/personDashboard/dynamicActionPlanV3/SharedActionPlanReport";
import SharedMobilityReportV95 from "../components/userDetail/personDashboard/mobilityReport/SharedMobilityReport";
import SharedMobilityReportV2 from "../components/userDetail/personDashboard/mobilityReport/SharedMobilityReportV2";
import { MOBILITY_REPORT_ENGINE } from "../utlis/mobilityReportEngine";
import GazeMetricsReportRoute from "../components/userDetail/personDashboard/gazeReport/GazeMetricsReportRoute";

import StsSide from "../components/userDetail/testModule/sts/StsSide";
import HowToTestStsSide from "../components/userDetail/testModule/sts/HowToTestStsSide";

const LocalReportQa =
  process.env.NODE_ENV !== "production"
    ? lazy(() => import("../qa/MobilityRenderQa"))
    : null;

// v95 iframe engine by default; the backend render document is opt-in.
const MobilityReport =
  MOBILITY_REPORT_ENGINE === "v2" ? MobilityReportV2 : MobilityReportV95;
const SharedMobilityReport =
  MOBILITY_REPORT_ENGINE === "v2"
    ? SharedMobilityReportV2
    : SharedMobilityReportV95;

const router = createBrowserRouter([
  ...(process.env.NODE_ENV !== "production"
    ? [
        {
          path: "/report-render-qa",
          element: (
            <Suspense fallback={null}>
              <LocalReportQa />
            </Suspense>
          ),
        },
      ]
    : []),
  // Gaze Metrics is a direct authenticated report route. It intentionally does
  // not mount App/Layout so its scoped token cannot establish or invalidate a
  // normal staff session or trigger theme/session side effects.
  {
    path: GAZE_METRICS_REPORT,
    element: <GazeMetricsReportRoute />,
  },
  {
    path: "",
    element: <App />,
    children: [
      {
        path: ACTION_PLAN_REPORT,
        element: <SharedActionPlanReport />,
      },
      {
        path: MOBILITY_REPORT_SHARED,
        element: <SharedMobilityReport />,
      },
      {
        path: "",
        element: <Layout />,
        children: [
          {
            path: "/",
            element: <MmtLogin />,
          },
          {
            path: LOGIN,
            element: <MmtLogin />,
          },
          {
            path: SIGNUP,
            element: <MmtSignupFlow />,
          },
          {
            path: FORGOT_PASSWORD,
            element: <MmtForgotPasswordFlow />,
          },
          {
            path: ADMIN_CHOICE,
            element: (
              <RequireSession>
                <AdminChoice />
              </RequireSession>
            ),
          },
          {
            path: PEOPLE_STATS,
            element: (
              <RequireSession>
                <PeopleStats />
              </RequireSession>
            ),
          },
          {
            path: CHANGE_PASSWORD,
            element: (
              <RequireSession>
                <MmtChangePassword />
              </RequireSession>
            ),
          },
          {
            path: USER_LIST,
            element: (
              <RequireSession>
                <MmtUserList />
              </RequireSession>
            ),
          },
          {
            path: ADD_TEST_PERSON,
            element: (
              <RequireSession>
                <MmtAddTestPerson />
              </RequireSession>
            ),
          },
          {
            path: EDIT_TEST_PERSON,
            element: (
              <RequireSession>
                <MmtEditTestPerson />
              </RequireSession>
            ),
          },
          {
            path: VIEW_TEST_PERSON,
            element: (
              <RequireSession>
                <MmtEditTestPerson />
              </RequireSession>
            ),
          },
          {
            path: PERSON_DASHBOARD,
            element: <PersonDashboard />,
          },
          {
            path: ACTIVITY_MONITORING,
            element: <ActivityMonitoring />,
          },
          {
            path: ACTIVITY_TREND,
            element: <ActivityTrend />,
          },
          {
            path: MOBILITY_REPORT,
            element: <MobilityReport />,
          },
          { path: STS_SIDE_INFO, element: <StsSide /> },
          { path: HOW_TO_TEST_STS_SIDE, element: <HowToTestStsSide /> },
          {
            path: TUG_SIDE_INFO,
            element: <TugSide />,
          },
          {
            path: TUG_FRONT_INFO,
            element: <TugFront />,
          },
          {
            path: HOW_TO_TEST_TUG_FRONT,
            element: <HowtoTestTugFront />,
          },
          {
            path: HOW_TO_TEST_TUG_SIDE,
            element: <HowtoTestTugSide />,
          },
          {
            path: TEST_HISTORY,
            element: <HistoryTab />,
          },
          {
            path: VITALS,
            element: <VitalsScreen />,
          },
        ],
      },
    ],
  },
]);
export default router;
